import * as RadixTooltip from "@radix-ui/react-tooltip"
import clsx from "clsx"
import { useEffect, useState } from "react"

import useIsDesktop from "@hooks/useIsDesktop"
import { useAboveLayersZIndex } from "@hooks/useLayerStack"

import BottomDrawer from "@atoms/BottomDrawer/BottomDrawer"
import DrawerHeader from "@atoms/BottomDrawer/DrawerHeader/DrawerHeader"

import styles from "./Hint.module.scss"
import { HintProps } from "./Hint.types"

function HintContent(props: Pick<HintProps, "title" | "body" | "colorScheme">) {
    const { title, body, colorScheme } = props

    const isDesktop = useIsDesktop()

    return (
        <div
            className={clsx(
                styles.base,
                styles[`${colorScheme}ColorScheme`],
                styles[isDesktop ? "tooltipMode" : "drawerMode"],
            )}
        >
            <div className={styles.indicator} />
            <div className={styles.content}>
                {title && <div className={styles.title}>{title}</div>}
                <div className={styles.body}>{body}</div>
            </div>
        </div>
    )
}

export default function Hint(props: HintProps) {
    const {
        colorScheme,
        title,
        body,
        isDisabled = false,
        isOpen: controlledIsOpen,
        onOpenChange = () => null,
        defaultPlacement,
        children,
    } = props

    const isDesktop = useIsDesktop()
    const [isHintOpen, setIsHintOpen] = useState(false)
    const zIndex = useAboveLayersZIndex()

    useEffect(() => {
        if (typeof controlledIsOpen === "boolean") {
            setIsHintOpen(controlledIsOpen)
        }
    }, [controlledIsOpen])

    useEffect(() => {
        if (isHintOpen !== undefined) {
            onOpenChange?.(isHintOpen)
        }
    }, [isHintOpen])

    const openHint = () => setIsHintOpen(true)

    if (isDisabled) {
        return <>{children}</>
    } else {
        return (
            <>
                {!isDesktop ? (
                    <>
                        <div className={styles.drawerTrigger} onClick={openHint}>
                            {children}
                        </div>
                        <BottomDrawer.Root isOpen={isHintOpen} onOpenChange={setIsHintOpen}>
                            <BottomDrawer.Content>
                                <DrawerHeader.Root />
                                <HintContent title={title} body={body} colorScheme={colorScheme} />
                            </BottomDrawer.Content>
                        </BottomDrawer.Root>
                    </>
                ) : (
                    <RadixTooltip.Provider delayDuration={0}>
                        <RadixTooltip.Root open={isHintOpen} onOpenChange={setIsHintOpen}>
                            <RadixTooltip.Trigger asChild={true}>
                                <div>{children}</div>
                            </RadixTooltip.Trigger>

                            <RadixTooltip.Portal>
                                <RadixTooltip.Content
                                    side={defaultPlacement}
                                    sideOffset={4}
                                    style={{ zIndex }}
                                    className={styles.tooltip}
                                >
                                    <HintContent title={title} body={body} colorScheme={colorScheme} />
                                    <RadixTooltip.Arrow width={12} height={6} className={styles.tooltipArrow} />
                                </RadixTooltip.Content>
                            </RadixTooltip.Portal>
                        </RadixTooltip.Root>
                    </RadixTooltip.Provider>
                )}
            </>
        )
    }
}
